//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import LoginRequiredException from '@/utils/LoginRequiredException';

// Framework-agnostic controller for the inactivity-timeout UX.  It mirrors the
// backend's sliding session TTL with a local expiry clock, and as that clock
// nears zero it either silently extends the session (when the user has
// interacted recently) or raises a warning modal (when they have not).  This
// keeps an idle session from being held open forever by automated background
// polling, while never interrupting a user who is actually working.
//
// Two activity signals feed it:
//   - recordServerActivity(): a genuine user-initiated API call succeeded, so
//     the backend just reset its timer; advance the local clock to match.
//   - recordInteraction(): a user input event happened (key, click, mouse move,
//     scroll, wheel, touch — the set is owned by SessionTimeout).  It advances
//     the local interaction time and, throttled, fires a proactive keepalive so
//     ordinary use keeps the session alive.  Note this means a moving cursor
//     extends the session: an inactivity timeout cannot defend against the
//     authenticated client itself, so presence (any input) is treated as
//     activity by design — see the SessionTimeout event list.
//
// State is shared across tabs of the same session via localStorage so a
// keepalive (or activity) in one tab extends the session for all of them, and
// the modal does not pop in a background tab while the user works in another.

// 'loggedout' means another tab of this browser session logged out and this
// tab should tear down too (distinct from 'expired', which is an inactivity
// timeout and shows the "session expired" notice).
type SessionStatus = 'idle' | 'active' | 'expired' | 'loggedout';

export interface SessionActivitySnapshot {
  status: SessionStatus;
  // True while the inactivity warning modal should be shown.
  warningActive: boolean;
  // Epoch ms when the session expires absent further activity (0 when idle).
  expiresAt: number;
}

const DEFAULT_TTL_MS = 60 * 60 * 1000;
// How long before expiry the controller acts: silently extend on recent
// interaction, otherwise raise the warning modal (whose countdown then runs
// out this same window).  The effective lead is clamped to half the TTL at
// runtime (see warningLeadMs) so a short TTL still gets a real warning window;
// it also serves as the "interacted recently" window.  Exported so the unit
// tests derive their advance times from it rather than hardcoding a value.
export const WARNING_LEAD_MS = 5 * 60 * 1000;
export const CHECK_INTERVAL_MS = 20 * 1000;
// Throttle cross-tab localStorage writes for interaction (which can fire on
// every keystroke); the local timestamp is always current regardless.
const INTERACTION_PERSIST_MS = 10 * 1000;
// Minimum spacing between proactive keepalives triggered by interaction, so a
// flood of events (mousemove fires continuously) can't spam the backend.
const KEEPALIVE_THROTTLE_MS = 60 * 1000;
// Coalesce the cross-tab write + subscriber notify on a request-heavy session:
// re-arming the clock every request would write localStorage and fire a storage
// event in every other tab each time, for a sub-second change no one observes.
const BROADCAST_COALESCE_MS = 5 * 1000;

const LS_EXPIRES_AT = 'mc-session-expires-at';
const LS_LAST_INTERACTION = 'mc-session-last-interaction';
const LS_LOGGED_OUT = 'mc-session-loggedout';

const readLs = (key: string): number => {
  try {
    const raw = localStorage.getItem(key);
    const n = raw == null ? 0 : Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
};

const writeLs = (key: string, value: number): void => {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // Storage disabled (private browsing); cross-tab sync degrades to
    // per-tab, which is still correct, just not coordinated.
  }
};

const clearLs = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Storage disabled; nothing to clear.
  }
};

class SessionActivityController {
  private ttlMs = DEFAULT_TTL_MS;
  private expiresAt = 0;
  private lastInteractionAt = 0;
  private lastInteractionPersist = 0;
  // When the session was last extended (server activity or keepalive).  Used to
  // throttle the proactive keepalive so a burst of interaction events (e.g.
  // mousemove) extends the session at most once per KEEPALIVE_THROTTLE_MS.
  private lastReupAt = 0;
  // When the expiry clock was last persisted/broadcast, to coalesce the
  // localStorage write + subscriber notify on request-heavy sessions.
  private lastBroadcastAt = 0;
  // In-flight extend() promise, so concurrent calls share one keepalive.
  private pendingExtend: Promise<void> | null = null;
  private checkTimer: ReturnType<typeof setInterval> | null = null;
  private running = false;
  private snapshot: SessionActivitySnapshot = {
    status: 'idle', warningActive: false, expiresAt: 0
  };
  private listeners = new Set<() => void>();

  // Sends POST /api/keepalive; wired in the composition root.  Resolves when
  // the backend has reset its timer (the request itself counts as activity).
  keepalive: () => Promise<void> = async () => {};

  configure(ttlSeconds: number | undefined): void {
    if (!ttlSeconds || ttlSeconds <= 0) return;
    const prevTtlMs = this.ttlMs;
    this.ttlMs = ttlSeconds * 1000;
    // If the TTL changes after start() already seeded the clock (the backend
    // session_ttl loaded late, or differs from the default), shift the live
    // deadline by the delta so the countdown stays in lockstep with the server
    // instead of waiting for the next real request to correct it.
    if (this.running && this.ttlMs !== prevTtlMs) {
      this.expiresAt = this.expiresAt - prevTtlMs + this.ttlMs;
      writeLs(LS_EXPIRES_AT, this.expiresAt);
      this.update({ status: this.snapshot.status,
                    warningActive: this.snapshot.warningActive,
                    expiresAt: this.expiresAt });
    }
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    const now = Date.now();
    // start() always coincides with a fresh authenticated request — login, or
    // session restore's /api/protected — that just slid the backend's timer, so
    // the session has a full TTL from now.  Initialize to now + TTL and
    // broadcast it; adopting an older stored value (a closed tab or a prior
    // session) would shorten the client clock and pop a premature warning.
    // Other tabs adopt the larger value via their storage listener / check().
    this.expiresAt = now + this.ttlMs;
    writeLs(LS_EXPIRES_AT, this.expiresAt);
    // Drop any stale logout broadcast from a previous session on this browser.
    clearLs(LS_LOGGED_OUT);
    this.lastInteractionAt = now;
    this.lastReupAt = now;
    window.addEventListener('storage', this.handleStorage);
    this.checkTimer = setInterval(this.check, CHECK_INTERVAL_MS);
    this.update({ status: 'active', warningActive: false,
                  expiresAt: this.expiresAt });
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    if (this.checkTimer != null) {
      clearInterval(this.checkTimer);
      this.checkTimer = null;
    }
    window.removeEventListener('storage', this.handleStorage);
    this.expiresAt = 0;
    // Clear the persisted deadline so a later login on this browser starts a
    // fresh window rather than inheriting this (now-ended) session's clock —
    // the logout request itself is non-background and would otherwise leave a
    // future deadline behind for the next login to adopt.
    clearLs(LS_EXPIRES_AT);
    clearLs(LS_LAST_INTERACTION);
    this.update({ status: 'idle', warningActive: false, expiresAt: 0 });
  }

  recordServerActivity(): void {
    if (!this.running || this.snapshot.status === 'expired') return;
    // While the warning is up, ignore automatic traffic (window focus-refetches,
    // in-flight poll responses) — the modal backdrop blocks genuine user
    // requests, so anything arriving now is automatic and must not silently
    // dismiss the prompt.  Only an explicit "Stay signed in" (extend) does.
    if (this.snapshot.warningActive) return;
    this.bumpExpiry();
  }

  // Reset the expiry clock to a full TTL and clear any pending warning.  The
  // internal expiresAt is always advanced (check() reads it), but the
  // localStorage write + subscriber notify are coalesced to at most once per
  // BROADCAST_COALESCE_MS — except when a warning is active, which must be
  // cleared promptly so the modal closes.  check() still reads the freshest
  // cross-tab value from localStorage, so coalescing only delays the broadcast,
  // never the local clock.
  private bumpExpiry(): void {
    const now = Date.now();
    this.lastReupAt = now;
    this.expiresAt = now + this.ttlMs;
    if (!this.snapshot.warningActive &&
        now - this.lastBroadcastAt < BROADCAST_COALESCE_MS)
      return;
    this.lastBroadcastAt = now;
    writeLs(LS_EXPIRES_AT, this.expiresAt);
    this.update({ status: 'active', warningActive: false,
                  expiresAt: this.expiresAt });
  }

  recordInteraction(): void {
    if (!this.running || this.snapshot.status === 'expired') return;
    const now = Date.now();
    this.lastInteractionAt = now;
    if (now - this.lastInteractionPersist > INTERACTION_PERSIST_MS) {
      this.lastInteractionPersist = now;
      writeLs(LS_LAST_INTERACTION, now);
    }
    // Proactively extend the session on genuine activity so an actively-used
    // session never drifts toward the warning, throttled so a flood of events
    // (mousemove) doesn't spam the backend.  Suppressed once the warning modal
    // is open: that prompt must require an explicit "Stay signed in", so
    // passive movement can't silently dismiss it.
    if (!this.snapshot.warningActive &&
        now - this.lastReupAt >= KEEPALIVE_THROTTLE_MS) {
      this.lastReupAt = now;
      this.extend();
    }
  }

  // The "stay signed in" action and the near-expiry silent extension both land
  // here.  Clear the warning and reset the clock immediately so the box closes
  // the instant the user clicks, rather than after the keepalive round-trip
  // (which would let the countdown tick visibly first).  The keepalive then
  // confirms with the backend; bumpExpiry (not recordServerActivity) because
  // this is the one explicit path allowed to clear the warning.
  extend(): Promise<void> {
    // Coalesce concurrent calls (a proactive reup overlapping a check-tick
    // extension, or rapid "Stay" clicks) so only one keepalive is in flight.
    if (this.pendingExtend)
      return this.pendingExtend;
    this.pendingExtend = this.runExtend()
      .finally(() => { this.pendingExtend = null; });
    return this.pendingExtend;
  }

  private async runExtend(): Promise<void> {
    const prevExpiresAt = this.expiresAt;
    const prevWarningActive = this.snapshot.warningActive;
    this.bumpExpiry();
    const optimisticExpiresAt = this.expiresAt;
    try {
      await this.keepalive();
    } catch (err) {
      if (err instanceof LoginRequiredException) {
        // 401 -> the backend session already expired.  The api client cleared
        // service-level state, but the React logout + redirect only fires via
        // the 'expired' status (or a later query 401) — so flip to 'expired'
        // now, otherwise the optimistic bump above would strand the user on the
        // protected UI behind a closed modal.
        this.update({ status: 'expired', warningActive: false,
                      expiresAt: this.expiresAt });
      } else {
        // Transient (non-expiry) failure: the backend never received the
        // keepalive, so undo the optimistic bump.  Otherwise the client clock
        // drifts ahead of the server and the user hits a surprise 401 (with no
        // warning) once the server-side session actually expires.  Only revert
        // if nothing else moved the clock during the await (another tab's
        // keepalive, or a concurrent real request) — don't clobber a newer,
        // genuinely-extended deadline.
        if (this.expiresAt === optimisticExpiresAt) {
          this.expiresAt = prevExpiresAt;
          writeLs(LS_EXPIRES_AT, prevExpiresAt);
          this.update({ status: 'active', warningActive: prevWarningActive,
                        expiresAt: prevExpiresAt });
        }
      }
    }
  }

  // Tell other tabs of this browser session to log out now.  Called on an
  // explicit user-initiated logout; receivers tear down via the 'loggedout'
  // status with a non-broadcasting logout, so there is no echo loop.  Affects
  // the same browser only — separate browsers are independent sessions.
  broadcastLogout(): void {
    writeLs(LS_LOGGED_OUT, Date.now());
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  getSnapshot = (): SessionActivitySnapshot => this.snapshot;

  private update(next: SessionActivitySnapshot): void {
    if (next.status === this.snapshot.status &&
        next.warningActive === this.snapshot.warningActive &&
        next.expiresAt === this.snapshot.expiresAt)
      return;
    this.snapshot = next;
    this.listeners.forEach(l => l());
  }

  // Effective warning lead, clamped to half the TTL.  Without this, a TTL
  // shorter than WARNING_LEAD_MS would leave expiresIn permanently inside the
  // lead window — firing a keepalive on every check tick for an active user and
  // never surfacing the warning.  Also used as the "interacted recently" window.
  private warningLeadMs(): number {
    return Math.min(WARNING_LEAD_MS, Math.floor(this.ttlMs / 2));
  }

  private check = (): void => {
    if (!this.running) return;
    const now = Date.now();
    // Pick up an extension made in another tab before deciding anything.
    const stored = readLs(LS_EXPIRES_AT);
    if (stored > this.expiresAt) {
      this.expiresAt = stored;
      // Another tab just extended the session; sync the keepalive throttle so
      // this tab doesn't fire a redundant proactive keepalive on next input.
      this.lastReupAt = now;
      this.update({ status: 'active', warningActive: false,
                    expiresAt: this.expiresAt });
    }
    this.lastInteractionAt =
      Math.max(this.lastInteractionAt, readLs(LS_LAST_INTERACTION));

    const expiresIn = this.expiresAt - now;
    const sinceInteraction = now - this.lastInteractionAt;

    if (now >= this.expiresAt) {
      // The component reacts to 'expired' by running the full logout+redirect.
      this.update({ status: 'expired', warningActive: false,
                    expiresAt: this.expiresAt });
      return;
    }
    const lead = this.warningLeadMs();
    if (expiresIn > lead) return;
    const interactedRecently = sinceInteraction <= lead;
    if (interactedRecently && !this.snapshot.warningActive) {
      this.extend();
    } else if (!interactedRecently && !this.snapshot.warningActive) {
      this.update({ status: 'active', warningActive: true,
                    expiresAt: this.expiresAt });
    }
  };

  private handleStorage = (e: StorageEvent): void => {
    if (!this.running) return;
    if (e.key === LS_EXPIRES_AT) {
      const v = readLs(LS_EXPIRES_AT);
      if (v > this.expiresAt) {
        this.expiresAt = v;
        // Sync the keepalive throttle to the extending tab (see check()).
        this.lastReupAt = Date.now();
        // Another tab extended the session — drop any warning here too.
        this.update({ status: 'active', warningActive: false, expiresAt: v });
      }
    } else if (e.key === LS_LAST_INTERACTION) {
      this.lastInteractionAt =
        Math.max(this.lastInteractionAt, readLs(LS_LAST_INTERACTION));
    } else if (e.key === LS_LOGGED_OUT && e.newValue != null) {
      // Another tab logged out — tear down here too.  SessionTimeout reacts to
      // 'loggedout' with a non-broadcasting logout, so this does not echo.
      this.update({ status: 'loggedout', warningActive: false, expiresAt: 0 });
    }
  };
}

export const sessionActivity = new SessionActivityController();
